import {
    AccountInfo,
    BrowserAuthError,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    NavigationClient,
    NavigationOptions,
    PublicClientApplication,
} from "@azure/msal-browser";
import jwt from "jsonwebtoken";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

export const msalConfig = {
    auth: {
        clientId: "bd3776bf-aee2-47b3-975f-87fd8667c3f2",
        authority: "https://login.microsoftonline.com/cc8d834f-85f9-44cc-88e6-3e5b8b390dea/v2.0",
        redirectUri: "/",
        postLogoutRedirectUri: "/",
    },
    system: {
        allowPlatformBroker: false, // Disables WAM Broker
        navigateToLoginRequestUrl: true, // Set false to prevent automatic navigation back to the original request URL
    },
    cache: {
        // Safari requires sessionStorage due to ITP; other browsers use localStorage
        cacheLocation:
            typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
                ? "sessionStorage"
                : "localStorage",
    },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
    scopes: ["bd3776bf-aee2-47b3-975f-87fd8667c3f2/.default"],
};

export class CustomNavigationClient extends NavigationClient {
    private router: AppRouterInstance;

    constructor(router: AppRouterInstance) {
        super();
        this.router = router;
    }

    /**
     * Navigates to other pages within the same web application
     * You can use the useRouter hook provided by next.js to take advantage of client-side routing
     * @param url
     * @param options
     */
    async navigateInternal(url: string, options: NavigationOptions) {
        const relativePath = url.replace(window.location.origin, "");
        if (options.noHistory) {
            this.router.replace(relativePath);
        } else {
            this.router.push(relativePath);
        }

        return false;
    }
}

export const getJWT = async (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
    const account = instance.getActiveAccount() ?? accounts.at(0);
    if (!account) {
        throw new Error("No account available for token request");
    }
    const tokenRequest = {
        ...loginRequest,
        account,
    };
    return await instance
        .acquireTokenSilent(tokenRequest)
        .then((response) => response.accessToken)
        .catch(async (error) => {
            // Handle errors that require user interaction
            const needsInteraction =
                error instanceof InteractionRequiredAuthError ||
                (error instanceof BrowserAuthError && error.errorCode === "monitor_window_timeout");

            if (needsInteraction) {
                await instance.acquireTokenRedirect(tokenRequest);
            }
            throw error;
        });
};

export const decodeJWT = (token: string) => {
    return jwt.decode(token);
};

export const isJwtExpired = (token: string): boolean => {
    const decoded = decodeJWT(token);
    if (decoded && typeof decoded === "object" && "exp" in decoded) {
        const exp = (decoded.exp ?? 0) * 1000;
        const now = Date.now();
        return now > exp;
    }
    return true;
};
